import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { useInverseBg } from '../theme/useInverseBg';
import { isEmpty } from 'lodash-es';

const EMPTY_VALUE = '-';

export interface LabelValueListContextType {
  active: boolean;
  inverse?: boolean;
  emptyValue?: React.ReactNode;
  alignRight?: boolean;
  intent?: 'inactive' | 'default';
  valueSpan: number;
}

export const LabelValueListContext =
  React.createContext<LabelValueListContextType>({
    active: false,
    inverse: false,
    emptyValue: EMPTY_VALUE,
    valueSpan: 1,
  });

type LabelValueListProps = React.PropsWithChildren<{
  /**
   * Inverzní verze - typicky používáme na tmavším pozadí.
   */
  inverse?: boolean;
  className?: string;
  /**
   * Co se má zobrazit při prázdné hodnotě? Ve vhochozím stavu se použije '-'. Děděné na items - použije se pokud není u items definováno.
   */
  emptyValue?: React.ReactNode;
  /**
   * Zarovna hodnoty doprava? Děděné na items - použije se pokud není u items definováno.
   */
  alignRight?: boolean;
  /**
   * Počet sloupců pro layout, hodnota 2 je výchozí a je doporučená pro většinu použití.
   */
  cols?: number;
  /**
   * Má se list roztáhnout na šířku? - používat POUZE toto, nikoli např. w-100 apod.
   */
  full?: boolean;
  /**
   * Intent labelu, pokud není uvedeno, je default.
   */
  intent?: 'inactive' | 'default';
}>;

export const LabelValueList: React.FC<LabelValueListProps> = ({
  children,
  className,
  inverse,
  emptyValue = EMPTY_VALUE,
  alignRight,
  cols,
  full,
  intent,
}) => {
  const parentInverse = useInverseBg(inverse);

  const rows = [];
  let dataCols: any[] = [];
  let dataColsSpans = 0;
  React.Children.forEach(children, (child: any) => {
    if (child) {
      const valueSpan = child.props.span || 1;
      const labelValueSpan = 1 + valueSpan; // label + value
      if (dataColsSpans + labelValueSpan > (cols || 2)) {
        if (dataColsSpans === 0) {
          throw new Error(
            'Chybná definice sloupců - šířka label + hodnota je pro jednu položku věší než počet sloupců'
          );
        }
        rows.push(<tr key={rows.length}>{dataCols}</tr>);
        dataCols = [];
        dataColsSpans = 0;
      }

      dataCols.push(
        <LabelValueListContext.Provider
          value={{
            active: true,
            valueSpan,
            emptyValue,
            alignRight,
            intent,
            inverse: parentInverse,
          }}
        >
          {child}
        </LabelValueListContext.Provider>
      );

      dataColsSpans += labelValueSpan;
    }
  });
  if (dataCols.length > 0) {
    rows.push(<tr key={rows.length}>{dataCols}</tr>);
  }

  if (isEmpty(rows)) {
    return null;
  } else {
    return (
      <table
        className={classNames(className, Classes.LABEL_VALUE_LIST, {
          [Classes.LABEL_VALUE_LIST_FULL]: full,
        })}
      >
        {rows}
      </table>
    );
  }
};
